import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import type { PaywallCouponAppliedProps } from "@/react/helpers/urlHelpers/internalApi/paywallCoupons";
import type { PaywallCoupon } from "@/react/types/PaywallCoupon";
import { internalApi } from "../helpers/urlHelpers";

interface PaywallCouponParams {
  [key: string]: unknown;
}

interface IndexParams {
  couponStatuses?: string[];
  paywallId?: number;
  skipDateValidations?: boolean;
}

const updatePaywallCoupon = (
  paywallCouponId: number,
  params: PaywallCouponParams,
): Promise<PaywallCoupon> => {
  const body = {
    paywall_coupon: params,
    forReactQuery: true,
  };

  return reactQueryPut<PaywallCoupon, typeof body>(
    internalApi.paywallCoupons.update({
      paywallCouponId: paywallCouponId,
    }),
    body,
  );
};

const createPaywallCoupon = (
  params: PaywallCouponParams,
): Promise<PaywallCoupon> => {
  const body = {
    paywall_coupon: params,
  };

  return reactQueryPost<PaywallCoupon>(
    internalApi.paywallCoupons.create(),
    body,
  );
};

const destroyPaywallCoupon = (paywallCouponId: number): Promise<void> =>
  reactQueryDelete<void, never>(
    internalApi.paywallCoupons.destroy({
      paywallCouponId: paywallCouponId,
    }),
  );

interface PaywallCouponCalculateDiscountResponse {
  checkout_duration_label: string;
  display_discount_with_currency: string;
  paywall_coupon: Record<string, unknown>;
  paywall_price: Record<string, unknown>;
  total_after_discount: number;
}

const calculateDiscount = (
  params: PaywallCouponAppliedProps,
): Promise<PaywallCouponCalculateDiscountResponse> =>
  reactQueryGet<PaywallCouponCalculateDiscountResponse>(
    internalApi.paywallCoupons.calculateDiscount({ params }),
  );

const index =
  ({ paywallId, skipDateValidations, couponStatuses }: IndexParams) =>
  () =>
    reactQueryGet<PaywallCoupon[]>(
      internalApi.paywallCoupons.index({
        params: {
          paywall_id: paywallId,
          skip_date_validations: skipDateValidations,
          status: couponStatuses,
        },
      }),
    );

const showPaywallCoupon = (paywallCouponId: number): Promise<PaywallCoupon> =>
  reactQueryGet<PaywallCoupon>(
    internalApi.paywallCoupons.show({ paywallCouponId }),
  );

export const paywallCouponApi = {
  index,
  createPaywallCoupon,
  updatePaywallCoupon,
  calculateDiscount,
  destroyPaywallCoupon,
  showPaywallCoupon,
};
